/**
 * FR5 — Insert scene dialog (mock 14): role, duration 4|6|8, grounding thesis
 * from the FR3 selection, chain re-anchor warning, slot counter. Prompts are
 * AI-drafted from brief + thesis ON insert (editable afterwards) — a recorded
 * deviation from the mock's pre-insert preview, saving a paid call per open.
 */

import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import type { AdCreativeVideoPlan } from '@segmently/schema-models/ad-creative';
import { StyledTextField } from '@modules/ui-components';
import { AD_CREATIVE_STORYBOARD } from '../../selectors';
import { INSIGHT_CATEGORY_COLOR } from '../../services/AdCreativeInsightContextClient';
import { VIDEO_USD_PER_SECOND, type ThesisOption } from './StoryboardWorkspace';

const SB = AD_CREATIVE_STORYBOARD;
const ROLE_CHOICES = ['hook', 'problem', 'demo', 'proof', 'benefit', 'objection', 'cta'] as const;

export interface InsertScenePayload {
  position: number;
  role: string;
  durationSec: number;
  sourcePath?: string;
}

interface InsertScenePopoverProps {
  plan: AdCreativeVideoPlan;
  position: number;
  thesisOptions: ThesisOption[];
  busy: boolean;
  onClose: () => void;
  onInsert: (payload: InsertScenePayload) => void;
}

export function InsertScenePopover({
  plan,
  position,
  thesisOptions,
  busy,
  onClose,
  onInsert,
}: InsertScenePopoverProps): React.ReactElement {
  const [role, setRole] = useState<string>('objection');
  const [durationSec, setDurationSec] = useState<number>(4);
  const [sourcePath, setSourcePath] = useState<string>('');

  const segments = useMemo(() => [...plan.segments].sort((a, b) => a.index - b.index), [plan.segments]);
  const before = segments[position - 1];
  const after = segments[position];
  const displacedChain = after?.continuityMode === 'chain' && (after.clipUrl || (after.takes?.length ?? 0) > 0);
  const totalAfter = segments.reduce((sum, segment) => sum + segment.durationSec, 0) + durationSec;
  const slotsAfter = segments.length + 1;

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth data-testid={SB.insertPopover}>
      <DialogTitle sx={{ fontSize: 15 }}>
        Insert scene {before ? `after ${before.index + 1} · ${(before.role ?? 'scene').toUpperCase()}` : 'at the start'}
        {after ? ` before ${after.index + 1} · ${(after.role ?? 'scene').toUpperCase()}` : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={1.5}>
            <StyledTextField
              select
              label="Scene role"
              data-testid={SB.insertRole}
              value={role}
              onChange={(value) => setRole(value)}
              size="small"
              fullWidth
            >
              {ROLE_CHOICES.map((choice) => (
                <MenuItem key={choice} value={choice}>{choice.toUpperCase()}</MenuItem>
              ))}
            </StyledTextField>
            <StyledTextField
              select
              label="Duration"
              data-testid={SB.insertDuration}
              value={String(durationSec)}
              onChange={(value) => setDurationSec(Number(value))}
              size="small"
              sx={{ width: 120 }}
            >
              {[4, 6, 8].map((seconds) => (
                <MenuItem key={seconds} value={String(seconds)}>{seconds}s</MenuItem>
              ))}
            </StyledTextField>
          </Stack>

          <StyledTextField
            select
            label="Grounding thesis (from your selection)"
            data-testid={SB.insertThesis}
            value={sourcePath}
            onChange={(value) => setSourcePath(value)}
            size="small"
            fullWidth
          >
            <MenuItem value="">No source (brand-generic)</MenuItem>
            {thesisOptions.map((option) => (
              <MenuItem key={option.sourcePath} value={option.sourcePath}>
                <Chip size="small" color={INSIGHT_CATEGORY_COLOR[option.category] ?? 'default'} label={option.category} sx={{ mr: 1, height: 18, fontSize: 10 }} />
                {option.label.slice(0, 60)}
              </MenuItem>
            ))}
          </StyledTextField>

          <Alert severity="info" icon={false} data-testid={SB.insertDraft} sx={{ py: 0.5 }}>
            ✨ Keyframe + motion prompts will be drafted from the brief{sourcePath ? ' + thesis' : ''} on insert — editable afterwards (text-only, cents).
          </Alert>

          {displacedChain && (
            <Alert severity="warning" data-testid={SB.insertChainWarning} sx={{ py: 0.5 }}>
              Scene {after!.index + 1} · {(after!.role ?? 'scene').toUpperCase()} uses chain continuity — its first frame will re-anchor to the NEW previous scene after it renders.
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary">
            The new scene renders alone (≈${(durationSec * VIDEO_USD_PER_SECOND).toFixed(2)} · estimated · {durationSec}s). Existing clips are untouched and re-stitched. Total becomes {totalAfter}s.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" data-testid={SB.insertSlotCounter} sx={{ flex: 1, pl: 1 }}>
          {slotsAfter} of 8 scene slots used after insert
        </Typography>
        <Button size="small" data-testid={SB.insertCancel} onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          size="small"
          variant="contained"
          data-testid={SB.insertConfirm}
          disabled={busy || slotsAfter > 8}
          startIcon={busy ? <CircularProgress size={12} /> : undefined}
          onClick={() => onInsert({ position, role, durationSec, sourcePath: sourcePath || undefined })}
        >
          Insert scene
        </Button>
      </DialogActions>
    </Dialog>
  );
}
